import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import {
  COUNTRY_STORE_CONFIG,
  getStoreConfig,
  getCountryName,
} from './countryUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RegionConfig {
  regionCode: string; // ISO country code uppercase: 'IN', 'US', 'AE', etc.
  defaultLanguage: string; // Default language: 'en', 'ja', 'de', 'th', 'fr'
  supportedLanguages: string[]; // All languages available
  shopifyStore: ShopifyStore;
  currency: Currency;
  countryName: string;
  baseStore: ShopifyStore | null; // Set if this store is aliased to another
}

export interface ParsedRegionLocale {
  region: string; // uppercase country code: 'US', 'JP', 'DE'
  language: string; // language code: 'en', 'ja', 'de'
  locale: string; // i18next locale: 'en-US', 'ja', 'de', 'th', 'fr'
  slug: string; // URL slug: 'us', 'jp', 'de-en'
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SUPPORTED_LANGUAGES = [
  'en',
  'ja',
  'de',
  'th',
  'fr',
  'zh',
  'zh-tw',
] as const;

/** Paths that should get the region prefix (customer-facing) */
/**
 * Cookie recording an explicit region choice made in the region selector.
 * Value is the chosen region slug (e.g. 'in', 'global', 'de-en').
 * The proxy uses it to avoid geo-bouncing users off a region they picked.
 */
export const REGION_PREFERENCE_COOKIE = 'uh_region_preference';
export const REGION_PREFERENCE_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

export const REGION_PREFIXED_PATHS = new Set([
  'ring',
  'ring-pro',
  'home',
  'rare',
  'shop',
  'pricing',
  'diesel-ultrahuman-ring',
  'x',
  'blood-vision',
  'm1',
  'womens-health',
  'powerplugs',
  'hsa-fsa',
  'heroes',
  'environment',
  'partners',
  'performance-lab',
  'photon',
  'retail',
  'one-tree-planted',
  'ultrahuman-x-clue',
  'pulsomics',
  'ultrasignal',
  'print-sizing-kit',
  'for-work',
  'window-of-opportunity',
  'troubleshoot',
  'privacypolicy',
  'termsandcondition',
  'termsofsale',
  'why-ultrahuman',
  'how-it-works',
  // Retired route (page deleted): must stay here so bare /luminary geo-
  // prefixes and reaches the luminaryRetired redirect rule (like environment).
  'luminary',
]);

/** Paths that should NOT get region prefix */
export const NON_PREFIXED_PATHS = new Set([
  'choose-country-region',
  'order',
  'science',
  'auth',
  'api',
  'ogdb',
  'forms',
  'gift-card',
  'cycle-report',
  'update-app',
  'prompt',
  'web-docs',
  '_next',
  'test',
  'tests',
  'timeout',
  'advisors',
  'join_group',
  'birthday',
  'investorRelations',
  'shader',
  'flex',
  'locales',
  'images',
]);

// ---------------------------------------------------------------------------
// Default-language mapping
// ---------------------------------------------------------------------------

const NON_ENGLISH_DEFAULTS: Record<string, string> = {
  JP: 'ja',
  DE: 'de',
  AT: 'de',
  TH: 'th',
  FR: 'fr',
  CN: 'zh',
  TW: 'zh-tw',
};

/**
 * Extra UI languages available in addition to the region default + English.
 * English-default regions listed here gain an optional secondary language
 * (e.g. CA → French via /ca-fr). Does NOT change store, currency, or checkout.
 */
const ADDITIONAL_LANGUAGES_BY_REGION: Record<string, string[]> = {
  CA: ['fr'],
};

// ---------------------------------------------------------------------------
// Language → i18next locale mapping
// ---------------------------------------------------------------------------

const LANGUAGE_LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  ja: 'ja',
  de: 'de',
  th: 'th',
  fr: 'fr',
  zh: 'zh',
  'zh-tw': 'zh-TW',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the default language for a region code.
 * Non-English-default countries: JP→'ja', DE/AT→'de', TH→'th', FR→'fr',
 * CN→'zh', TW→'zh-tw'.
 * All others default to 'en'.
 */
export function getDefaultLanguageForRegion(regionCode: string): string {
  return NON_ENGLISH_DEFAULTS[regionCode.toUpperCase()] ?? 'en';
}

/**
 * Maps a short language code to its i18next locale string.
 * 'en'→'en-US', 'ja'→'ja', 'de'→'de', 'th'→'th', 'fr'→'fr',
 * 'zh'→'zh', 'zh-tw'→'zh-TW'
 */
export function languageToLocale(language: string): string {
  return LANGUAGE_LOCALE_MAP[language.toLowerCase()] ?? 'en-US';
}

/**
 * Determine which languages are supported for a given region (UI only).
 * - Default language first
 * - English always available when it is not the default
 * - Optional extras from ADDITIONAL_LANGUAGES_BY_REGION (e.g. CA → fr)
 *
 * Store / currency / product availability stay region-driven — language never
 * changes the purchase backend.
 */
export function getSupportedLanguagesForRegion(regionCode: string): string[] {
  const code = regionCode.toUpperCase();
  const defaultLang = getDefaultLanguageForRegion(code);
  const languages: string[] = [defaultLang];
  if (defaultLang !== 'en') languages.push('en');
  for (const lang of ADDITIONAL_LANGUAGES_BY_REGION[code] ?? []) {
    if (!languages.includes(lang)) languages.push(lang);
  }
  return languages;
}

// ---------------------------------------------------------------------------
// Core functions
// ---------------------------------------------------------------------------

/**
 * Parse a URL slug (e.g. 'us', 'jp', 'de-en') into its constituent parts.
 *
 * Slug formats:
 *  - '{cc}'       → uses the default language for that country
 *  - '{cc}-{lang}'→ explicit language override
 *
 * Unknown region codes fall back to ROW config with language 'en'.
 */
export function parseRegionLocale(slug: string): ParsedRegionLocale {
  const lower = slug.toLowerCase();

  // "global" maps to ROW with English
  if (lower === 'global') {
    return {
      region: 'GLOBAL',
      language: 'en',
      locale: 'en-US',
      slug: 'global',
    };
  }

  const parts = lower.split('-');

  let regionRaw: string;
  let languageRaw: string | undefined;

  if (parts.length >= 2 && parts[0].length === 2) {
    regionRaw = parts[0];
    languageRaw = parts[1];
  } else {
    regionRaw = lower;
  }

  const region = regionRaw.toUpperCase();

  // Determine language: explicit > default for region
  const language = languageRaw
    ? (SUPPORTED_LANGUAGES as readonly string[]).includes(languageRaw)
      ? languageRaw
      : getDefaultLanguageForRegion(region)
    : getDefaultLanguageForRegion(region);

  const locale = languageToLocale(language);
  const builtSlug = buildRegionLocaleSlug(region, language);

  return { region, language, locale, slug: builtSlug };
}

/**
 * Build a URL slug from a region code and optional language.
 *
 * Rules:
 *  - If the language is the region's default → just the lowercase country code ('us', 'jp')
 *  - Otherwise → '{cc}-{lang}' ('de-en', 'jp-en')
 *  - If language is omitted, uses the region's default.
 */
export function buildRegionLocaleSlug(
  region: string,
  language?: string,
): string {
  const code = region.toUpperCase();

  // Unknown countries → "global" slug (ROW)
  if (!(code in COUNTRY_STORE_CONFIG)) {
    return 'global';
  }

  const defaultLang = getDefaultLanguageForRegion(code);
  const lang = language ?? defaultLang;

  if (lang === defaultLang) {
    return code.toLowerCase();
  }
  return `${code.toLowerCase()}-${lang.toLowerCase()}`;
}

const regionConfigCache = new Map<string, RegionConfig>();

/**
 * Build a full RegionConfig for a given country code.
 * Uses getStoreConfig() from countryUtils.ts internally; falls back to ROW.
 * Results are cached per region code.
 */
export function getRegionConfig(regionCode: string): RegionConfig {
  const code = regionCode.toUpperCase();
  let cached = regionConfigCache.get(code);
  if (!cached) {
    const storeConfig = getStoreConfig(code);
    cached = {
      regionCode: code,
      defaultLanguage: getDefaultLanguageForRegion(code),
      supportedLanguages: getSupportedLanguagesForRegion(code),
      shopifyStore: storeConfig.store,
      currency: storeConfig.currency,
      countryName: getCountryName(code),
      baseStore: storeConfig.baseStore ?? null,
    };
    regionConfigCache.set(code, cached);
  }
  return cached;
}

/** Pre-computed at module load for all valid region slugs. */
export const ALL_REGION_SLUGS: string[] = (() => {
  const slugs: string[] = [];
  for (const code of Object.keys(COUNTRY_STORE_CONFIG)) {
    if (code === 'ROW') continue;
    const defaultLang = getDefaultLanguageForRegion(code);
    slugs.push(code.toLowerCase());
    for (const lang of getSupportedLanguagesForRegion(code)) {
      if (lang !== defaultLang) {
        slugs.push(`${code.toLowerCase()}-${lang.toLowerCase()}`);
      }
    }
  }
  slugs.push('global');
  return slugs;
})();

/**
 * Returns all valid URL slugs — one per country in COUNTRY_STORE_CONFIG,
 * plus language-variant slugs for every non-default supported language
 * (e.g. de-en, th-en, ca-fr).
 */
export function getAllRegionSlugs(): string[] {
  return ALL_REGION_SLUGS;
}

/**
 * Map a region code to its BCP 47 hreflang value (e.g. 'US' → 'en-US').
 */
export function regionToHrefLang(regionCode: string): string {
  const upper = regionCode.toUpperCase();
  const lang = getDefaultLanguageForRegion(upper).split('-')[0];
  return `${lang}-${upper}`;
}

/**
 * BCP 47 hreflang for a region-locale slug. Language-variant slugs (e.g.
 * 'de-en') use the explicit language; primary subtag only so hyphenated codes
 * ('zh-tw') stay valid. Shared by lib/seo.ts metadata + generateSiteMap XML.
 */
export function regionSlugToHrefLang(slug: string): string {
  const parsed = parseRegionLocale(slug);
  return slug.includes('-')
    ? `${parsed.language.split('-')[0]}-${parsed.region}`
    : regionToHrefLang(parsed.region);
}

/**
 * Validate whether a slug maps to a real region.
 * Accepts any 2-letter ISO code (since unknown countries fall back to ROW),
 * plus {cc}-{lang} patterns where the language is supported.
 */
export function isValidRegionSlug(slug: string): boolean {
  const lower = slug.toLowerCase();

  // "global" is a special slug for ROW (Rest of World)
  if (lower === 'global') return true;

  const parts = lower.split('-');

  if (!/^[a-z]{2}$/.test(parts[0])) return false;

  // Simple 2-letter code — always valid (unknown regions fall back to ROW)
  if (parts.length === 1) return true;

  // {cc}-{lang} pattern
  if (parts.length === 2) {
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(parts[1]);
  }

  return false;
}

/** Canonical slug for a stored preference, or null when it is not a valid slug. */
export function parseRegionPreference(value?: string | null): string | null {
  if (!value) return null;
  const normalized = value.toLowerCase();
  return ALL_REGION_SLUGS.includes(normalized) ? normalized : null;
}

/** Serialize the region preference consistently for browser and test use. */
export function serializeRegionPreference(
  slug: string,
  secure: boolean,
): string | null {
  const canonicalSlug = parseRegionPreference(slug);
  if (!canonicalSlug) return null;

  return [
    `${REGION_PREFERENCE_COOKIE}=${canonicalSlug}`,
    'Path=/',
    `Max-Age=${REGION_PREFERENCE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    secure ? 'Secure' : null,
  ]
    .filter(Boolean)
    .join('; ');
}
